/** @format */

import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Chip,
  Skeleton,
  Tooltip,
  LinearProgress,
  Button,
} from "@mui/material";
import {
  RocketLaunch,
  AccessTime,
  TrendingUp,
  School,
  Star,
  AttachMoney,
  Speed,
  EmojiObjects,
  WorkspacePremium,
  ArrowForward,
  OpenInNew,
} from "@mui/icons-material";

// Reuse STYLES from GetInsights patterns
const STYLES = {
  colors: {
    primary: "#000088",
    secondary: "#262655",
    accent: "#4A3D9E",
    textPrimary: "#1A1A1A",
    textSecondary: "#888",
    border: "#D5D9EB",
    borderLight: "#E3E6F0",
    background: "#F3FBFF",
    backgroundWhite: "#FFFFFF",
  },
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
  borderRadius: {
    card: "24px",
    small: "12px",
  },
  boxShadow: {
    card: "0 2px 16px 0 rgba(0,0,0,0.06)",
    cardHover: "0 4px 24px 0 rgba(0,0,0,0.10)",
  },
};

// Priority color mapping
const PRIORITY_CONFIG = {
  High: { color: "#E53E3E", bg: "#FFF5F5", label: "High Priority" },
  Medium: { color: "#DD6B20", bg: "#FFFAF0", label: "Medium Priority" },
  Low: { color: "#38A169", bg: "#F0FFF4", label: "Low Priority" },
};

// Learning curve config
const LEARNING_CURVE_CONFIG = {
  Low: { value: 30, color: "#38A169", label: "Easy" },
  Medium: { value: 60, color: "#DD6B20", label: "Moderate" },
  High: { value: 90, color: "#E53E3E", label: "Advanced" },
};

// Pricing badge config
const PRICING_CONFIG = {
  Free: { color: "#38A169", bg: "#F0FFF4", icon: "✓" },
  Freemium: { color: "#3182CE", bg: "#EBF8FF", icon: "◐" },
  Paid: { color: "#805AD5", bg: "#FAF5FF", icon: "$" },
};

// ROI star rating renderer
const renderRoiStars = (rating) => {
  const match = rating?.match(/(\d)/);
  const count = match ? parseInt(match[1]) : 0;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.3,
      }}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          sx={{
            fontSize: 16,
            color: i <= count ? "#F6AD55" : "#E2E8F0",
          }}
        />
      ))}
      <Typography
        sx={{
          fontSize: "12px",
          fontWeight: 600,
          color: STYLES.colors.textSecondary,
          ml: 0.5,
        }}
      >
        {rating}
      </Typography>
    </Box>
  );
};

// --- Summary Stats Cards ---
const SummaryCard = ({ icon, label, value, gradient }) => (
  <Box
    sx={{
      flex: "1 1 0",
      minWidth: { xs: "100%", sm: "140px" },
      background: gradient,
      borderRadius: "16px",
      p: { xs: "16px", sm: "20px" },
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 1,
      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      transition: "transform 0.2s, box-shadow 0.2s",
      "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
      },
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(255,255,255,0.7)",
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontFamily: STYLES.typography.fontFamily,
        fontSize: { xs: "20px", sm: "24px" },
        fontWeight: 800,
        color: STYLES.colors.secondary,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontFamily: STYLES.typography.fontFamily,
        fontSize: { xs: "11px", sm: "12px" },
        fontWeight: 500,
        color: STYLES.colors.textSecondary,
        textAlign: "center",
        lineHeight: 1.3,
      }}
    >
      {label}
    </Typography>
  </Box>
);

// --- Tool Card Component ---
const ToolCard = ({ tool, index, isExpanded, onToggle }) => {
  const priority =
    PRIORITY_CONFIG[tool.adoption_priority] || PRIORITY_CONFIG.Medium;
  const learningCurve =
    LEARNING_CURVE_CONFIG[tool.learning_curve] || LEARNING_CURVE_CONFIG.Medium;
  const pricing = PRICING_CONFIG[tool.pricing] || PRICING_CONFIG.Paid;

  return (
    <Box
      onClick={onToggle}
      sx={{
        background: STYLES.colors.backgroundWhite,
        borderRadius: "20px",
        border: isExpanded
          ? `2px solid ${STYLES.colors.accent}`
          : `1.5px solid ${STYLES.colors.borderLight}`,
        overflow: "hidden",
        cursor: "pointer",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: isExpanded
          ? STYLES.boxShadow.cardHover
          : STYLES.boxShadow.card,
        "&:hover": {
          boxShadow: STYLES.boxShadow.cardHover,
          transform: "translateY(-1px)",
        },
      }}
    >
      {/* Card Header */}
      <Box
        sx={{
          p: { xs: "16px", sm: "20px 24px" },
          display: "flex",
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
        }}
      >
        {/* Rank Badge */}
        <Box
          sx={{
            width: { xs: 36, sm: 44 },
            height: { xs: 36, sm: 44 },
            borderRadius: "12px",
            background: "linear-gradient(135deg, #6652E4, #4A3D9E)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              fontSize: { xs: "14px", sm: "18px" },
              fontWeight: 800,
              color: "#fff",
            }}
          >
            #{tool.rank}
          </Typography>
        </Box>

        {/* Tool Info */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <Typography
              sx={{
                fontFamily: STYLES.typography.fontFamily,
                fontSize: { xs: "15px", sm: "18px" },
                fontWeight: 700,
                color: STYLES.colors.secondary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {tool.name}
            </Typography>
            <Chip
              label={tool.category}
              size="small"
              sx={{
                fontFamily: STYLES.typography.fontFamily,
                fontSize: "10px",
                fontWeight: 600,
                height: "22px",
                background: "#EDF2F7",
                color: STYLES.colors.secondary,
                border: "none",
              }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              fontSize: { xs: "12px", sm: "13px" },
              color: STYLES.colors.textSecondary,
              mt: 0.3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {tool.purpose}
          </Typography>
        </Box>

        {/* Quick Stats */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Productivity Gain" arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TrendingUp sx={{ fontSize: 16, color: "#38A169" }} />
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "#38A169",
                }}
              >
                +{tool.productivity_gain}
              </Typography>
            </Box>
          </Tooltip>
          <Tooltip title="Time Saved/Week" arrow>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: "#3182CE" }} />
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#3182CE",
                }}
              >
                {tool.time_saved_per_week}
              </Typography>
            </Box>
          </Tooltip>
          <Chip
            label={pricing.icon + " " + tool.pricing}
            size="small"
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              fontSize: "11px",
              fontWeight: 600,
              height: "24px",
              background: pricing.bg,
              color: pricing.color,
              border: `1px solid ${pricing.color}20`,
            }}
          />
        </Box>

        {/* Expand Arrow */}
        <ArrowForward
          sx={{
            fontSize: 18,
            color: STYLES.colors.textSecondary,
            transition: "transform 0.3s",
            transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            flexShrink: 0,
          }}
        />
      </Box>

      {/* Expanded Details */}
      {isExpanded && (
        <Box
          sx={{
            p: { xs: "0 16px 16px", sm: "0 24px 24px" },
            borderTop: `1px solid ${STYLES.colors.borderLight}`,
            pt: { xs: 2, sm: 2.5 },
          }}
        >
          {/* Why Recommended */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <EmojiObjects sx={{ fontSize: 18, color: "#DD6B20" }} />
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "13px",
                  fontWeight: 800,
                  color: STYLES.colors.secondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Why Recommended
              </Typography>
            </Box>
            <Typography
              sx={{
                fontFamily: STYLES.typography.fontFamily,
                fontSize: { xs: "13px", sm: "14px" },
                color: STYLES.colors.textPrimary,
                lineHeight: 1.6,
              }}
            >
              {tool.why_recommended}
            </Typography>
          </Box>

          {/* Metrics Grid */}
          <Grid container spacing={1.5}>
            {/* Productivity & Time */}
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  background: "#F0FFF4",
                  borderRadius: "12px",
                  p: 1.5,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrendingUp sx={{ fontSize: 20, color: "#38A169", mb: 0.5 }} />
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#38A169",
                  }}
                >
                  +{tool.productivity_gain}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: STYLES.colors.textSecondary,
                  }}
                >
                  Productivity
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box
                sx={{
                  background: "#EBF8FF",
                  borderRadius: "12px",
                  p: 1.5,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AccessTime sx={{ fontSize: 20, color: "#3182CE", mb: 0.5 }} />
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "18px",
                    fontWeight: 800,
                    color: "#3182CE",
                  }}
                >
                  {tool.time_saved_per_week}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: STYLES.colors.textSecondary,
                  }}
                >
                  Saved/Week
                </Typography>
              </Box>
            </Grid>
            {/* ROI & Priority */}
            <Grid item xs={6} sm={3} sx={{ mt: { xs: 3, sm: 0 } }}>
              <Box
                sx={{
                  background: "#FFFAF0",
                  borderRadius: "12px",
                  p: 1.5,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <WorkspacePremium
                  sx={{ fontSize: 20, color: "#DD6B20", mb: 0.5 }}
                />
                {renderRoiStars(tool.roi_rating)}
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: STYLES.colors.textSecondary,
                    mt: 0.3,
                  }}
                >
                  ROI Rating
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3} sx={{ mt: { xs: 3, sm: 0 } }}>
              <Box
                sx={{
                  background: priority.bg,
                  borderRadius: "12px",
                  p: 1.5,
                  textAlign: "center",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Speed sx={{ fontSize: 20, color: priority.color, mb: 0.5 }} />
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "14px",
                    fontWeight: 700,
                    color: priority.color,
                  }}
                >
                  {tool.adoption_priority}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: STYLES.colors.textSecondary,
                  }}
                >
                  Adoption Priority
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Learning Curve & Pricing Row */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mt: 4,
              mb: 1,
              flexWrap: "wrap",
            }}
          >
            {/* Learning Curve */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flex: 1,
                minWidth: { xs: "100%", sm: "300px" },
              }}
            >
              <School sx={{ fontSize: 18, color: learningCurve.color }} />
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "13px",
                  fontWeight: 600,
                  color: STYLES.colors.textSecondary,
                  whiteSpace: "nowrap",
                }}
              >
                Learning Curve
              </Typography>
              <LinearProgress
                variant="determinate"
                value={learningCurve.value}
                sx={{
                  flex: 1,
                  mx: 1,
                  height: 6,
                  borderRadius: "3px",
                  backgroundColor: "#EDF2F7",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: learningCurve.color,
                    borderRadius: "3px",
                  },
                }}
              />
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "13px",
                  fontWeight: 700,
                  color: learningCurve.color,
                }}
              >
                {learningCurve.label}
              </Typography>
            </Box>

            {/* Pricing Badge */}
            <Chip
              label={`${pricing.icon}  ${tool.pricing}`}
              sx={{
                fontFamily: STYLES.typography.fontFamily,
                fontSize: "13px",
                fontWeight: 600,
                height: "32px",
                background: pricing.bg,
                color: pricing.color,
                border: `1px solid ${pricing.color}30`,
                "& .MuiChip-label": { px: 1.5 },
              }}
            />
          </Box>

          {/* Best For Tags */}
          {tool.best_for && tool.best_for.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  fontSize: "11px",
                  fontWeight: 600,
                  color: STYLES.colors.textSecondary,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  mb: 0.8,
                }}
              >
                Best For
              </Typography>
              <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
                {tool.best_for.map((item, i) => (
                  <Chip
                    key={i}
                    label={item}
                    size="small"
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontSize: "11px",
                      fontWeight: 500,
                      height: "26px",
                      background: "linear-gradient(135deg, #EBF4FF, #E9D8FD)",
                      color: STYLES.colors.accent,
                      border: "none",
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Visit Website Button */}
          {tool.url && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent: { xs: "center", sm: "flex-end" },
              }}
            >
              <Button
                variant="contained"
                href={tool.url}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNew />}
                sx={{
                  fontFamily: STYLES.typography.fontFamily,
                  background: `linear-gradient(135deg, ${STYLES.colors.primary}, ${STYLES.colors.accent})`,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "20px",
                  width: { xs: "100%", sm: "auto" },
                  px: 4,
                  py: 1,
                  boxShadow: "0 4px 14px 0 rgba(0,0,0,0.15)",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${STYLES.colors.accent}, ${STYLES.colors.primary})`,
                    boxShadow: "0 6px 20px 0 rgba(0,0,0,0.2)",
                  },
                }}
              >
                Explore Tool
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

// --- Loading Skeleton ---
export const AiToolsSkeleton = () => (
  <Box>
    {/* Summary skeletons */}
    <Box
      sx={{
        display: "flex",
        gap: 2,
        mb: 3,
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      {[1, 2, 3].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          width={160}
          height={120}
          sx={{ borderRadius: "16px" }}
        />
      ))}
    </Box>
    {/* Tool card skeletons */}
    {[1, 2, 3].map((i) => (
      <Skeleton
        key={`tool-${i}`}
        variant="rounded"
        height={80}
        sx={{ mb: 1.5, borderRadius: "20px" }}
      />
    ))}
  </Box>
);

// === MAIN COMPONENT ===
function AiToolsSection({
  aiToolsData,
  aiToolsLoading,
  aiToolsError,
  checkSkill,
}) {
  const [expandedIndex, setExpandedIndex] = useState(0); // First tool expanded by default

  const handleToggle = (index) => {
    setExpandedIndex((prev) => (prev === index ? -1 : index));
  };

  // Don't render if no data or there is an error (and not loading)
  if (!aiToolsLoading && (!aiToolsData || aiToolsError)) return null;

  return (
    <Grid item xs={12}>
      <Box
        id="ai-tools-section"
        sx={{
          border: "1px solid #000088",
          borderRadius: "39px",
          background: STYLES.colors.background,
          height: "100%",
          boxSizing: "inherit",
        }}
        className="ai-tools-section"
        p={{ xs: 2, sm: 4, md: 3, lg: 5, xl: 5 }}
      >
        {/* Section Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            mb: { xs: 2, sm: 3 },
          }}
        >
          <RocketLaunch sx={{ fontSize: 36, color: "#DD6B20" }} />
          <Typography
            color="primary"
            variant="h6"
            sx={{
              fontFamily: STYLES.typography.fontFamily,
              textAlign: "center",
              fontWeight: 700,
              color: STYLES.colors.primary,
            }}
          >
            Productivity Tools & Resources for {checkSkill}
          </Typography>
        </Box>

        {/* Loading State */}
        {aiToolsLoading && <AiToolsSkeleton />}

        {/* Data Loaded */}
        {!aiToolsLoading && aiToolsData && !aiToolsError && (
          <>
            {/* Profession Summary */}
            {aiToolsData.profession_summary && (
              <Box
                sx={{
                  background:
                    "linear-gradient(135deg, #EBF4FF 0%, #E9D8FD 100%)",
                  borderRadius: "16px",
                  p: { xs: 2, sm: 2.5 },
                  mb: 2.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: { xs: "13px", sm: "14px" },
                    color: STYLES.colors.secondary,
                    lineHeight: 1.6,
                    textAlign: "center",
                  }}
                >
                  {aiToolsData.profession_summary}
                </Typography>
              </Box>
            )}

            {/* Summary Stats */}
            <Box
              sx={{
                display: "flex",
                gap: { xs: 1.5, sm: 2 },
                mb: 3,
                flexWrap: "wrap",
                justifyContent: "center",
              }}
            >
              <SummaryCard
                icon={<TrendingUp sx={{ fontSize: 22, color: "#38A169" }} />}
                label="Est. Productivity Gain"
                value={aiToolsData.estimated_productivity_gain || "N/A"}
                gradient="linear-gradient(135deg, #F0FFF4, #C6F6D5)"
              />
              <SummaryCard
                icon={<AccessTime sx={{ fontSize: 22, color: "#3182CE" }} />}
                label="Time Saved Per Week"
                value={aiToolsData.estimated_time_saved_per_week || "N/A"}
                gradient="linear-gradient(135deg, #EBF8FF, #BEE3F8)"
              />
              <SummaryCard
                icon={<RocketLaunch sx={{ fontSize: 22, color: "#805AD5" }} />}
                label="Tools Recommended"
                value={`${aiToolsData.tools?.length || 0} Tools`}
                gradient="linear-gradient(135deg, #FAF5FF, #E9D8FD)"
              />
            </Box>

            {/* Top Challenges */}
            {aiToolsData.top_challenges &&
              aiToolsData.top_challenges.length > 0 && (
                <Box
                  sx={{
                    background: STYLES.colors.backgroundWhite,
                    borderRadius: "14px",
                    border: `1.5px solid ${STYLES.colors.borderLight}`,
                    p: { xs: 1.5, sm: 2 },
                    mb: 2.5,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: STYLES.typography.fontFamily,
                      fontSize: "12px",
                      fontWeight: 700,
                      color: STYLES.colors.textSecondary,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      mb: 1,
                    }}
                  >
                    Top Challenges These Tools Address
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {aiToolsData.top_challenges.map((challenge, i) => (
                      <Chip
                        key={i}
                        label={challenge}
                        sx={{
                          fontFamily: STYLES.typography.fontFamily,
                          fontSize: "12px",
                          fontWeight: 500,
                          background: "#FFF5F5",
                          color: "#C53030",
                          border: "1px solid #FED7D7",
                          "& .MuiChip-label": { px: 1.5 },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

            {/* Tools List */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {aiToolsData.tools?.map((tool, index) => (
                <ToolCard
                  key={tool.rank || index}
                  tool={tool}
                  index={index}
                  isExpanded={expandedIndex === index}
                  onToggle={() => handleToggle(index)}
                />
              ))}
            </Box>

            {/* Workflow Summary Footer */}
            {aiToolsData.workflow_summary && (
              <Box
                sx={{
                  mt: 2.5,
                  p: { xs: 2, sm: 2.5 },
                  background: STYLES.colors.backgroundWhite,
                  borderRadius: "14px",
                  border: `1.5px dashed ${STYLES.colors.border}`,
                  textAlign: "center",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: "11px",
                    fontWeight: 600,
                    color: STYLES.colors.textSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    mb: 0.5,
                  }}
                >
                  Your Typical Workflow
                </Typography>
                <Typography
                  sx={{
                    fontFamily: STYLES.typography.fontFamily,
                    fontSize: { xs: "13px", sm: "14px" },
                    color: STYLES.colors.textPrimary,
                    lineHeight: 1.6,
                  }}
                >
                  {aiToolsData.workflow_summary}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Grid>
  );
}

export default AiToolsSection;
